import React from "react";
import {connect} from "react-redux";
import {updatePage} from "../../actions/page";
import {
    checkContentWriterGenerationStatus,
    createContentWriterArticle, deleteContentWriterArticle,
    loadContentWriterArticle, rewriteSelectedText,
    updateContentWriterArticle
} from "../../actions/content_writer";
import {displayErrors} from "../../libs/utils";
import {InlineLoading, Loading} from "../../libs/loading";
import {Button, Card, Col, message, Modal, Row, Space, Tag, Tooltip} from "antd";
import ContentEditor from "../../components/content_editor/ContentEditor";
import jsonToHtml from "../../components/content_editor/EditorJsonToHTML";
import {debounce} from "lodash";
import moment from "moment";
import GenerateArticleModal from "../../components/content/GenerateArticleModal";
import {ArrowLeftOutlined, ExclamationCircleOutlined} from "@ant-design/icons";
import {Link} from "react-router-dom";
import {BiSave, BiSolidMagicWand, BiTrash} from "react-icons/bi";


class ArticleEditor extends React.Component {
    state = {
        title: '',
        editorJson: null,
        initialJson: null,
        loadingArticle: false,
        initialLoad: true,
        lastSaved: null,
        savingArticle: false,
        showGenerateArticleModal: false,
        generationRequestId: null,
        isGeneratingArticle: false,
        generatingArticleCompleted: false,
        wordCount: 0,
        deletingArticle: false,
        generationStep: 'Waiting to start'
    }

    articleGeneratorTimer = null;

    constructor(props) {
        super(props);
        this.debouncedSave = debounce(this.saveArticle.bind(this), 1500);
    }

    componentWillUnmount() {
        this.debouncedSave.cancel();

        if (this.articleGeneratorTimer) {
            clearInterval(this.articleGeneratorTimer);
        }
    }

    contentEditorRef = React.createRef();

    componentDidMount() {
        if(typeof(this.props.match.params.article_guid) !== 'undefined') {
            this.loadArticle();
        } else {
            this.props.createContentWriterArticle(this.props.match.params.guid, {}, (res) => {
               this.props.history.push('/content/' + this.props.match.params.guid + '/articles/' + res.data.guid);
            }, (err) => {
                if(typeof err.response !== 'undefined') {
                    displayErrors(err.response.data);
                }
            });
        }
    }

    loadArticle() {
        this.setState({ loadingArticle: true });

        this.props.loadContentWriterArticle(this.props.match.params.guid, this.props.match.params.article_guid, (res) => {
                this.props.updatePage(<span>{res.data.title} <Tag color='warning'>Beta</Tag></span>);

                let data = {
                    loadingArticle: false,
                    title: res.data.title,
                    generationRequestId: res.data.generation_request_id,
                };

                if (res.data.html && !res.data.json) {
                    const html = res.data.html;
                    const api = this.contentEditorRef.current;

                    if (api?.waitReady) {
                        api.waitReady()
                            .then(() => api.setHTML(html))
                            .catch(() => {
                                setTimeout(() => {
                                    this.contentEditorRef.current?.setHTML?.(html);
                                }, 150);
                            });
                    } else {
                        setTimeout(() => {
                            this.contentEditorRef.current?.setHTML?.(html)
                        }, 150);
                    }
                } else if (res.data.json) {
                    data.editorJson = res.data.json;
                    data.initialJson = res.data.json;
                }

                if (res.data.generation_request_id !== null) {
                    data.isGeneratingArticle = true;
                    this.articleGeneratorTimer = setInterval(this.checkArticleGenerationStatus, 2000);
                }

                this.setState(data);

                window.scrollTo({ top: 0, behavior: 'smooth' });
            },
            (err) => {
                if (typeof err.response !== "undefined") {
                    this.setState({ loadingArticle: false });
                    displayErrors(err.response.data);
                }
            }
        );
    }

    triggerSaveArticle() {
        this.debouncedSave.cancel();
        this.saveArticle();
    }

    saveArticle() {
        if(this.state.initialLoad) {
            this.setState({ initialLoad: false });
            return;
        }

        if(this.state.isGeneratingArticle) {
            return;
        }

        if(this.state.savingArticle) {
            return;
        }

        if(this.state.deletingArticle) {
            return;
        }

        let data = {
            title: this.state.title,
            html: this.contentEditorRef.current?.getHTML(),
            json: this.state.editorJson
        };

        this.setState({ savingArticle: true });

        this.props.updateContentWriterArticle(this.props.match.params.guid, this.props.match.params.article_guid, data, () => {
            this.setState({ savingArticle: false, lastSaved: moment().format('YYYY/MM/DD HH:mm:ss') });
        }, (err) => {
            if(typeof err.response !== 'undefined') {
                this.setState({ savingArticle: false });
                displayErrors(err.response.data);
            }
        });
    }

    handleEditorChange = (json) => {
        const newJsonStr = JSON.stringify(json);

        if (newJsonStr !== this.lastJsonStr) {
            this.lastJsonStr = newJsonStr;
            this.setState({ editorJson: json });
            this.debouncedSave();
        }
    };

    handleTitleChange = (title) => {
        this.setState({ title });
        this.props.updatePage(<span>{title} <Tag color='warning'>Beta</Tag></span>);
        this.debouncedSave();
    }

    checkArticleGenerationStatus = async () => {
        this.props.checkContentWriterGenerationStatus(this.state.generationRequestId, (res) => {
            this.setState({ generationStep: res.data.step });

            if(res.data.status === 'COMPLETED') {
                this.setState({ isGeneratingArticle: false, generatingArticleCompleted: true });
                clearInterval(this.articleGeneratorTimer);
                this.loadArticle();

                setTimeout(() => {
                    this.setState({ generatingArticleCompleted: false });
                }, 1200);
            }

        }, (err) => {
            if(typeof err.response !== 'undefined') {
                displayErrors(err.response.data);
            }
        });
    }

    async rewriteWithAI(text, prompt) {
        let response = text;

        let data = {
            'selected_text': text,
            'user_prompt': prompt,
            'article': jsonToHtml(this.contentEditorRef)
        }

        await this.props.rewriteSelectedText(this.props.match.params.guid, this.props.match.params.article_guid, data).then(res => {
            response = text.replace(text, res.data);
        }).catch(err => {
            if(typeof err.response !== 'undefined') {
                displayErrors(err.response.data);
            }
        });

        return response;
    }

    deleteArticle() {
        const { confirm } = Modal;

        confirm({
            title: 'Are you sure?',
            centered: true,
            okText: 'Yes',
            okType: 'danger',
            icon: <ExclamationCircleOutlined />,
            content: 'Are you sure you want to delete this article?',
            onOk: () => {
                this.props.deleteContentWriterArticle(this.props.match.params.guid, this.props.match.params.article_guid, () => {
                    this.setState({ deletingArticle: true }, () => {
                        message.success('Article deleted successfully');
                        this.props.history.push('/content/' + this.props.match.params.guid);
                    });

                }, (err) => {
                    this.setState({ loadingArticles: false, searching: false });

                    if(typeof err.response !== 'undefined') {
                        displayErrors(err.response.data);
                    }
                });
            }
        })
    }

    onWordCountChange = (value) => {
        this.setState({ wordCount: value });
    }

    render() {
        return <>
            <div className={this.state.isGeneratingArticle || this.state.generatingArticleCompleted ? 'generating-article-overlay-container' : ''}>
                {this.state.isGeneratingArticle || this.state.generatingArticleCompleted ? <div
                    className={'generating-article-overlay ' + (this.state.isGeneratingArticle ? ' fade-in' : '') + (this.state.generatingArticleCompleted ? ' fade-out' : '')}>
                    <div className="magic-bg"></div>
                    <div className="magic-aurora"></div>
                    <div className="loading-content">
                        <Loading/>
                        <div className="generating-article-overlay-text">
                            <p>{this.state.generationStep}...</p>
                        </div>
                    </div>
                </div> : ''}
            </div>
            <Row gutter={[16, 16]} style={{paddingBottom: '10px'}} justify='space-between'>
                <Col>
                    <Space>
                        <Link to={'/content/' + this.props.match.params.guid}><Button icon={<ArrowLeftOutlined />}>Back</Button></Link>
                        <Space.Compact>

                            <Tooltip title='Generate Article'>
                                <Button onClick={() => this.setState({ showGenerateArticleModal: true, generatingArticleCompleted: false})} type='primary'
                                        icon={<BiSolidMagicWand style={{marginBottom: '-2px', marginRight: '5px'}}/>}>
                                    Generate Article
                                </Button>
                            </Tooltip>
                            <Tooltip title='Save Article'>
                                <Button onClick={() => this.triggerSaveArticle()} icon={<BiSave style={{marginBottom: '-2px', marginRight: '5px'}} />}>
                                    Save Article
                                </Button>
                            </Tooltip>
                        </Space.Compact>
                    </Space>
                </Col>
                <Col>
                    <Tooltip title='Delete Article'>
                        <Button onClick={() => this.deleteArticle()} icon={<BiTrash style={{marginBottom: '-2px', marginRight: '5px'}} />}>
                            Delete
                        </Button>
                    </Tooltip>
                </Col>
            </Row>
            <Card size='small' style={{height: '100%'}}>
                <Row style={{height: '100%'}}>
                    <Col span={16} offset={4} style={{height: '100%'}}>
                        <ContentEditor
                            editorRef={this.contentEditorRef}
                            onJsonChange={this.handleEditorChange}
                            initialJson={this.state.initialJson}
                            onTitleChange={this.handleTitleChange}
                            onSaveContent={() => this.triggerSaveArticle()}
                            onGenerateContent={() => this.setState({
                                showGenerateArticleModal: true,
                                generatingArticleCompleted: false
                            })}
                            onWordCountChange={this.onWordCountChange}
                            rewriteWithAI={(selectedText, aiPrompt) => this.rewriteWithAI(selectedText, aiPrompt)}
                            onDeleteArticle={() => this.deleteArticle()}
                            contentBottom={<Row gutter={[16, 16]} justify='space-between'>
                                <Col className='editor-small-text'>
                                    Word count: {this.state.wordCount}
                                </Col>
                                <Col className='editor-small-text'>
                                    {this.state.savingArticle ? <div style={{display: 'inline'}}><InlineLoading style={{fontSize: '13px', color: '#737373', marginRight: '5px'}} /> Saving...</div> : this.state.lastSaved ? 'Last saved at: ' + this.state.lastSaved : ''}
                                </Col>
                            </Row>}
                        />
                    </Col>
                </Row>

                <GenerateArticleModal
                    showModal={this.state.showGenerateArticleModal}
                    projectId={this.props.match.params.guid}
                    articleId={this.props.match.params.article_guid}
                    close={(requestId) => {
                        this.setState({
                            showGenerateArticleModal: false
                        });

                        if (requestId !== null) {
                            this.setState({
                                isGeneratingArticle: true,
                                generationRequestId: requestId
                            }, () => this.articleGeneratorTimer = setInterval(this.checkArticleGenerationStatus, 3000));
                        }
                    }}
                />
            </Card>
        </>
    }
}

export default connect(null, {
    updatePage, loadContentWriterArticle, checkContentWriterGenerationStatus,
    updateContentWriterArticle, createContentWriterArticle, rewriteSelectedText,
    deleteContentWriterArticle
})(ArticleEditor);